// @system — product identity config (template defaults)
// DO NOT edit this file for product-specific values — use @custom/info.js instead.
// This file is overwritten during template sync.

/**
 * Social-proof quote on the auth brand panel (pages/static/@system/AuthPage).
 * @typedef {object} AuthTestimonial
 * @property {string} quote
 * @property {string} name
 * @property {string} role
 * @property {string} [initials]
 */

/**
 * Left brand panel of the auth page. The generated @custom/info.js spreads
 * `baseInfo.auth` and sets `testimonial: null` (no fabricated social proof).
 * @typedef {object} AuthPanelInfo
 * @property {string} [badge]
 * @property {string} [description]
 * @property {string[]} [features]
 * @property {AuthTestimonial | null} [testimonial]
 * @property {{ val: string, sub: string }[]} [stats]
 */

/**
 * Subscription plan for route-based gating (routes/@system/utils.js isRouteLocked).
 * @typedef {object} RoutePlan
 * @property {string} name
 * @property {string} [priceId]
 * @property {string[]} [noAllowedRoutes]   routes this plan CANNOT access ('/app/admin/*' wildcards allowed)
 */

/**
 * One brand.json `pricing.plans[]` entry as PricingPage consumes it. brand.schema.json
 * declares `pricing` as a free-form object today; this is the shape the client relies on.
 * @typedef {object} BrandPlan
 * @property {string} plan            slug — 'free' renders the free tier
 * @property {string} name
 * @property {string} [price]         display price, digits are parsed out ('$29' → 2900 cents)
 * @property {string} [interval]      '/month' | '/year'
 * @property {string} [description]
 * @property {string[]} [features]
 * @property {boolean} [popular]
 * @property {string} [cta]
 */

/** brand.json `pricing` block, emitted into @custom/info.js by scripts/apply-brand.js when present. */
/**
 * brand.json `pricing`: the plan list plus, only when the business set them, a guarantee sentence and the
 * name of its payment processor (PricingPage says neither on its own).
 * @typedef {{ plans?: BrandPlan[], guarantee?: string, paymentProcessor?: string }} BrandPricing
 */

/**
 * Product identity as `import { info } from '@/config'` resolves it: this file's
 * defaults shallow-merged with the generated @custom/info.js `customInfo`.
 * @typedef {object} ProductInfo
 * @property {string} name
 * @property {string} tagline
 * @property {string} description
 * @property {string} [metaDescription]             description clamped to ≤ 160 characters for <meta name="description">
 *   (scripts/lib/meta-description.cjs, written by apply-brand; read by components/@system/OgMeta)
 * @property {string} companyAddress
 * @property {string} legalEffectiveDate             '' when never set (brand.json legal.effectiveDate)
 * @property {string} [legalCountry]                 the business's country for the governing-law clause (brand.json legal.country)
 * @property {string} [legalStatus]                  'template' | 'reviewed' (brand.json legal.status; lib/@system/legal.js)
 * @property {string} url
 * @property {string} supportEmail
 * @property {Record<string, string>} social        platform → URL; omit keys to hide icons
 * @property {string} brandColor
 * @property {string} accentColor
 * @property {string} defaultTheme                  'light' | 'dark' (string: the generated file is not narrowed)
 * @property {Partial<Record<'light' | 'dark', { primary?: string, accent?: string, onPrimary?: string }>>} [brandModes]
 *   per colour mode primary/accent as resolved into brand.css — written by scripts/apply-brand.js when
 *   brand.json carries modes.*; read by lib/@system/brandPrePaint so the first paint uses each theme's own primary
 * @property {string} logo                          square mark
 * @property {string} [logoUrl]                     full wordmark (generated file only)
 * @property {string} logoDark
 * @property {string} logoWhite
 * @property {string} [favicon]
 * @property {{ heading: string, body: string, mono: string }} [brandFonts]
 * @property {AuthPanelInfo} auth
 * @property {RoutePlan[]} plans
 * @property {BrandPricing} [pricing]               only when brand.json has a `pricing` block
 */

/** @type {ProductInfo} */
export const info = {
  name: 'ProductTemplate',
  tagline: 'Your product tagline here',
  description: 'a software-as-a-service platform',
  companyAddress: '',
  // Legal facts come from brand.json `legal` (Orkosi provisioning → apply-brand → @custom/info.js):
  // no invented effective date, no guessed country; 'template' until the business reviewed the text
  // (lib/@system/legal.js). The pages hide what is empty and say what is unreviewed.
  legalEffectiveDate: '',
  legalCountry: '',
  legalStatus: 'template',
  url: import.meta.env.VITE_APP_URL ?? 'http://localhost:5173',
  // The business's own support mailbox (brand.json supportEmail). Empty = the pages link to /contact
  // instead; never a mailbox on a domain nobody owns.
  supportEmail: '',
  // Social links — set product URLs in @custom/info.js. Omit keys to hide icons.
  social: {},
  // Brand colors — must match primaryColor / accentColor from products table.
  // Products override in @custom/info.js. CSS vars are injected automatically (see main.jsx).
  brandColor: '#64748B',
  accentColor: '#94A3B8',
  // Default theme — must match default_theme from products table.
  // Products override in @custom/info.js. Drives ThemeProvider initial state.
  defaultTheme: 'light',
  // Logo paths — auto-asset pipeline reads from /assets/logos/ (source: assets/logos/).
  // Products drop logo files in assets/logos/ and the template serves them everywhere.
  // Products override in @custom/info.js to use their own logos.
  logo: '/assets/logos/logo-mark.svg',
  logoDark: '/assets/logos/logo-white.svg',
  logoWhite: '/assets/logos/logo-white.svg',
  // Auth page — left brand panel content. Products override in @custom/info.js.
  auth: {
    // Empty by default: the auth pages fall back to the product's tagline and name and hide
    // the feature list, quote and figures. Products fill these in @custom/info.js — never
    // developer-template marketing or a fabricated testimonial on a customer site.
    badge: '',
    description: '',
    features: [],
    testimonial: null,
    stats: [],
  },
  // Subscription plans — used by isRouteLocked() for route-based feature gating.
  // Products define their plans in @custom/info.js.
  // Each plan: { name, priceId?, noAllowedRoutes: ['/app/route-to-lock', '/app/admin/*'] }
  // noAllowedRoutes = routes that this plan CANNOT access (locked behind higher plan).
  // Example: free plan locks /app/admin/*, /app/integrations, /app/api-keys
  plans: [],
}
